import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import SwitchToggle from 'components/Switch';
import { ThemePalette } from 'mui.theme';
import { TableFilter } from './TableFilter';
import { ColumnSelectFilter, ColumnTextFilter } from './ColumnFilters';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { addLiveDataToAssets } from 'views/FleetOverview/helpers/asset.helpers';
import { type Asset } from 'types/asset.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { insightStaticSummary, isInsightMarkedResolved } from './Details/utils';
import type {
  DetectionType,
  InsightsColumnFilters,
  InsightsTableNavigationState,
  InsightsTableProps,
  TableInsightItem,
} from 'types/ml-insights.types';
import {
  DETECTION_TYPE_OPTIONS,
  isHiddenInsightType,
} from 'types/ml-insights.types';
import MlInsightsHandler from 'handlers/ml-insights.handler';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const severityOrder = ['Low', 'Medium', 'High', 'Very High'];

/**
 * Detection mechanism for a row. Prefers the `detectionType` sent by
 * ml-insights-v2 and falls back to `source` so rows still classify correctly
 * against backends that predate that field.
 */
const resolveDetectionType = (item: TableInsightItem): DetectionType | null => {
  const detectionType = item.detectionType?.trim().toLowerCase();
  if (detectionType === 'ai predicted') return 'AI Predicted';
  if (detectionType === 'threshold') return 'Threshold';

  const source = item.source?.trim().toLowerCase();
  if (source === 'mlnotification') return 'AI Predicted';
  if (source === 'notification') return 'Threshold';

  return null;
};

export const InsightsTable = ({
  enableDarkTheme,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  selectedView,
  setSelectedView,
  selectedType,
  setSelectedType,
  selectedIssues,
  setSelectedIssues,
  restoredState,
  resetSignal,
}: InsightsTableProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { assets } = useAppSelector((state) => state.assetReducer);
  const { latestTagData } = useAppSelector((state) => state.deviceReducer);
  const assetsWithLiveData = useMemo(
    () => addLiveDataToAssets(assets, latestTagData),
    [assets, latestTagData]
  );
  const navigate = useNavigate();
  const {
    data: { insights },
  } = useAppSelector((state) => state.mlInsightsReducer);
  const [tableData, setTableData] = useState<TableInsightItem[]>(insights);
  const [updatingStatusIds, setUpdatingStatusIds] = useState<string[]>([]);
  const [resolveConfirmNotificationId, setResolveConfirmNotificationId] =
    useState<string | null>(null);
  const [page, setPage] = useState<number>(restoredState?.page ?? 0);
  const [rowsPerPage] = useState(restoredState?.rowsPerPage ?? 10);
  const emptyColumnFilters: InsightsColumnFilters = {
    insightName: '',
    assetId: '',
    system: '',
    severity: '',
    detectionType: '',
  };
  const [columnFilters, setColumnFilters] =
    useState<InsightsColumnFilters>(emptyColumnFilters);
  const hasActiveColumnFilters = Object.values(columnFilters).some(
    (value) => value.trim().length > 0
  );
  /** Stacks a header label above its column filter inside one header cell. */
  const headerCellSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '6px',
    minWidth: '110px',
  };
  const hasRestoredPageRef = useRef(restoredState === undefined);
  const previousFiltersRef = useRef<string | null>(null);
  const previousDatasetRef = useRef<string | null>(null);
  const mlInsightsHandler = new MlInsightsHandler();

  const uniqueIssues = Array.from(
    new Set(
      insights
        .filter((item) => !isHiddenInsightType(item as TableInsightItem))
        .map((item) => (item as unknown as TableInsightItem).issue)
        .filter((issue): issue is string => !!issue)
    )
  ).sort();

  useEffect(() => {
    // Hidden insight types are dropped before any other filter so they never
    // reach the rows, the issue dropdown, or the pagination count.
    let filtered = insights.filter(
      (item) => !isHiddenInsightType(item as unknown as TableInsightItem)
    );

    if (selectedView) {
      filtered = filtered.filter((item) => {
        const row = item as TableInsightItem;
        const resolution = row?.resolution;
        const endTsRaw = row.alertEndTs;
        const hasEndTs =
          typeof endTsRaw === 'string'
            ? endTsRaw.trim().length > 0
            : Boolean(endTsRaw);

        if (selectedView === 'Solved') {
          return resolution === 'Inactive' || resolution === 'Resolved';
        }

        if (selectedView === 'Inactive') {
          return resolution === 'Active' && hasEndTs;
        }

        if (selectedView === 'Active') {
          return resolution === 'Active' && !hasEndTs;
        }

        return resolution === selectedView;
      });
    }

    if (selectedType !== 'All') {
      filtered = filtered.filter(
        (item) => (item as unknown as TableInsightItem).type === selectedType
      );
    }

    if (selectedIssues.length > 0) {
      filtered = filtered.filter((item) =>
        selectedIssues.includes(
          (item as unknown as TableInsightItem).issue ?? ''
        )
      );
    }

    if (searchTerm) {
      const normalizeText = (text: string) =>
        text.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim();

      const normalizedSearchTerm = normalizeText(searchTerm);

      filtered = filtered.filter((item) => {
        const assetId = assetsWithLiveData.find(
          (asset: Asset) => asset.device.deviceId === item.device
        )?.bumperNumber;
        const issue = (item as unknown as TableInsightItem).issue;
        if (!issue) return false;
        const normalizedIssue = normalizeText(issue);

        return [
          normalizedIssue.includes(normalizedSearchTerm),
          assetId?.includes(normalizedSearchTerm) ?? false,
        ].some(Boolean);
      });
    }

    // Per-column filters. These run after the tab/type/issue/search filters, so
    // they always narrow the current tab's rows rather than the whole dataset.
    /** Matches the label the Insight Name cell renders. */
    const displayedIssue = (item: TableInsightItem) =>
      item.issue?.includes('Clogged Air Filter')
        ? 'Engine Air Filter Clogging'
        : item.issue ?? '';

    const insightNameQuery = columnFilters.insightName?.trim().toLowerCase();
    if (insightNameQuery) {
      filtered = filtered.filter((item) =>
        displayedIssue(item as unknown as TableInsightItem)
          .toLowerCase()
          .includes(insightNameQuery)
      );
    }

    const assetIdQuery = columnFilters.assetId?.trim().toLowerCase();
    if (assetIdQuery) {
      filtered = filtered.filter((item) => {
        const assetId = assetsWithLiveData.find(
          (asset: Asset) => asset.device.deviceId === item.device
        )?.bumperNumber;
        return String(assetId ?? '')
          .toLowerCase()
          .includes(assetIdQuery);
      });
    }

    const systemQuery = columnFilters.system?.trim().toLowerCase();
    if (systemQuery) {
      filtered = filtered.filter((item) => {
        const issueKey = displayedIssue(item as unknown as TableInsightItem);
        const system =
          insightStaticSummary[issueKey as keyof typeof insightStaticSummary]
            ?.subsystem ?? '';
        return system.toLowerCase().includes(systemQuery);
      });
    }

    const severityQuery = columnFilters.severity?.trim().toLowerCase();
    if (severityQuery) {
      filtered = filtered.filter(
        (item) =>
          ((item as unknown as TableInsightItem).severity ?? '')
            .trim()
            .toLowerCase() === severityQuery
      );
    }

    const detectionTypeQuery = columnFilters.detectionType
      ?.trim()
      .toLowerCase();
    if (detectionTypeQuery) {
      filtered = filtered.filter(
        (item) =>
          (resolveDetectionType(item as unknown as TableInsightItem) ?? '')
            .trim()
            .toLowerCase() === detectionTypeQuery
      );
    }

    filtered.sort((a: any, b: any) => {
      let comparison = 0;
      switch (sortBy) {
        case 'Insight Start':
          comparison =
            new Date(a?.alertStartTs ?? a?.timestamp ?? '').getTime() -
            new Date(b?.alertStartTs ?? b?.timestamp ?? '').getTime();
          break;

        case 'Severity':
          comparison =
            severityOrder.indexOf(a?.severity ?? '') -
            severityOrder.indexOf(b?.severity ?? '');
          break;

        default:
          return 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    const datasetKey = JSON.stringify(
      insights.map((item) => ({
        device: item.device,
        insight: item.insight,
        timestamp: item.timestamp,
      }))
    );
    const datasetChanged =
      previousDatasetRef.current !== null &&
      previousDatasetRef.current !== datasetKey;
    previousDatasetRef.current = datasetKey;

    setTableData(filtered);

    const filtersKey = JSON.stringify({
      sortBy,
      sortOrder,
      searchTerm,
      selectedView,
      selectedType,
      selectedIssues,
      columnFilters,
      resetSignal,
    });

    const filtersChanged = previousFiltersRef.current !== filtersKey;
    previousFiltersRef.current = filtersKey;

    const maxPage = Math.max(Math.ceil(filtered.length / rowsPerPage) - 1, 0);

    if (!hasRestoredPageRef.current && restoredState) {
      setPage(Math.min(restoredState.page ?? 0, maxPage));
      hasRestoredPageRef.current = true;
    } else if (datasetChanged) {
      setPage(0);
    } else if (filtersChanged) {
      setPage(0);
    } else {
      setPage((prevPage) => Math.min(prevPage, maxPage));
    }
  }, [
    sortBy,
    sortOrder,
    searchTerm,
    selectedView,
    selectedType,
    selectedIssues,
    columnFilters,
    insights,
    assetsWithLiveData,
    rowsPerPage,
    restoredState,
    resetSignal,
  ]);

  const paginatedData = tableData.slice(
    page * rowsPerPage,
    page * rowsPerPage + Number(rowsPerPage)
  );

  return (
    <Box
      sx={{
        padding: '20px 0px 20px 0px',
        backgroundColor: palette.boxBackground,
        borderRadius: '5px',
        border: `1px solid rgba(255, 255, 255, 0.15)`,
        marginTop: '20px',
      }}
    >
      <TableFilter
        enableDarkTheme={enableDarkTheme}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedIssues={selectedIssues}
        setSelectedIssues={setSelectedIssues}
        uniqueIssues={uniqueIssues}
      />

      <Box sx={{ width: '100%', marginTop: '20px' }}>
        <TableContainer
          sx={{
            color: 'white',
            overflow: 'scroll',
          }}
        >
          <Table
            sx={{
              minWidth: 850,
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
              },
            }}
            size="small"
          >
            <TableHead
              sx={{
                backgroundColor: palette.tableHeader,
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                >
                  <Box sx={headerCellSx}>
                    {t('insight_name')}
                    <ColumnTextFilter
                      value={columnFilters.insightName}
                      onChange={(value) => {
                        setColumnFilters((prev) => ({
                          ...prev,
                          insightName: value,
                        }));
                      }}
                      placeholder={t('filter')}
                      palette={palette}
                      enableDarkTheme={enableDarkTheme}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="left"
                >
                  <Box sx={headerCellSx}>
                    {t('asset_id')}
                    <ColumnTextFilter
                      value={columnFilters.assetId}
                      onChange={(value) => {
                        setColumnFilters((prev) => ({
                          ...prev,
                          assetId: value,
                        }));
                      }}
                      placeholder={t('filter')}
                      palette={palette}
                      enableDarkTheme={enableDarkTheme}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="left"
                >
                  <Box sx={headerCellSx}>
                    {t('insights_col_system')}
                    <ColumnTextFilter
                      value={columnFilters.system}
                      onChange={(value) => {
                        setColumnFilters((prev) => ({
                          ...prev,
                          system: value,
                        }));
                      }}
                      placeholder={t('filter')}
                      palette={palette}
                      enableDarkTheme={enableDarkTheme}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="center"
                >
                  <Box sx={{ ...headerCellSx, minWidth: '100px' }}>
                    {t('severity')}
                    <ColumnSelectFilter
                      value={columnFilters.severity}
                      onChange={(value) => {
                        setColumnFilters((prev) => ({
                          ...prev,
                          severity: value,
                        }));
                      }}
                      options={severityOrder}
                      placeholder={t('all')}
                      palette={palette}
                      enableDarkTheme={enableDarkTheme}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="center"
                >
                  <Box sx={{ ...headerCellSx, minWidth: '120px' }}>
                    {t('insight_source')}
                    <ColumnSelectFilter
                      value={columnFilters.detectionType}
                      onChange={(value) => {
                        setColumnFilters((prev) => ({
                          ...prev,
                          detectionType: value,
                        }));
                      }}
                      options={DETECTION_TYPE_OPTIONS}
                      placeholder={t('all')}
                      palette={palette}
                      enableDarkTheme={enableDarkTheme}
                    />
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="center"
                >
                  {t('alert_start_time')}
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="center"
                >
                  {t('alert_end_time')}
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="center"
                >
                  {t('count_of_threshold_breaches')}
                </TableCell>
                <TableCell
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 500,
                    py: 1.5,
                    verticalAlign: 'top',
                  }}
                  align="left"
                >
                  <Box sx={headerCellSx}>
                    {t('insights_col_resolved')}
                    {hasActiveColumnFilters && (
                      <Typography
                        onClick={() => {
                          setColumnFilters(emptyColumnFilters);
                        }}
                        sx={{
                          fontSize: '11px',
                          fontWeight: 400,
                          color: palette.primary,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {t('clear_all')}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>

            {/* Body */}
            <TableBody>
              {paginatedData.map(
                (itemData: TableInsightItem, index: number) => {
                  if (
                    !assets.find(
                      (asset: Asset) =>
                        asset.device.deviceId === itemData.device
                    )
                  ) {
                    return null;
                  }
                  const item = { ...itemData };
                  if (!item.device.includes('symbot')) {
                    item.device = `symbot${itemData.device}`;
                  }

                  const issue = item.issue?.includes('Clogged Air Filter')
                    ? 'Engine Air Filter Clogging'
                    : item.issue;
                  const assetId =
                    assetsWithLiveData.find(
                      (asset: Asset) =>
                        asset.device.deviceId === itemData.device
                    )?.bumperNumber ?? '-';
                  return (
                    <TableRow
                      key={index}
                      sx={{
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${palette.cartesianGrid}`,
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            textDecoration: 'underline dotted',
                            color: palette.primary,
                            textDecorationColor: palette.primary,
                            textDecorationThickness: '2px',
                            textUnderlineOffset: '4px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            const selectedInsightId = `${itemData.device}-${itemData.insight}`;
                            const navigationState: {
                              data: TableInsightItem;
                              tableState: InsightsTableNavigationState;
                            } = {
                              data: item,
                              tableState: {
                                page,
                                rowsPerPage,
                                paginatedData: paginatedData.map((entry) => ({
                                  ...entry,
                                })),
                                selectedInsightId,
                                filters: {
                                  sortBy,
                                  sortOrder,
                                  searchTerm,
                                  selectedView,
                                  selectedType,
                                  selectedIssues,
                                  resetSignal,
                                },
                              },
                            };

                            navigate('/insights/details', {
                              state: navigationState,
                            });
                          }}
                        >
                          {item.issue ?? item.insight ?? 'Unknown Issue'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="left"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {assetId}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: palette.textPrimary,
                          width: '200px',
                          py: 2,
                        }}
                        align="left"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {insightStaticSummary[
                            (issue ?? '') as keyof typeof insightStaticSummary
                          ]?.subsystem ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            backgroundColor:
                              item.severity === 'Very High'
                                ? '#B71C1C' // darker red
                                : item.severity === 'High'
                                ? '#EB5757'
                                : item.severity === 'Medium'
                                ? '#FFA403'
                                : '#ED7A00',
                            borderRadius: '4px',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                          }}
                        >
                          {item.severity ?? ''}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {resolveDetectionType(
                            item as unknown as TableInsightItem
                          ) ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {convertUtcToDisplayTime(
                            item.alertStartTs ?? item.timestamp ?? '',
                            preferredTimeDisplayMode,
                            timezone
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {item.alertEndTs
                            ? convertUtcToDisplayTime(
                                item.alertEndTs,
                                preferredTimeDisplayMode,
                                timezone
                              )
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                      >
                        <Typography sx={{ fontSize: '12px' }}>
                          {item.alterCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell
                        scope="row"
                        sx={{
                          color: palette.textPrimary,
                          py: 2,
                        }}
                        align="center"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <SwitchToggle
                          checked={isInsightMarkedResolved(
                            item.resolution,
                            item.status
                          )}
                          disabled={
                            !item.notificationId ||
                            updatingStatusIds.includes(item.notificationId)
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          onChange={(event) => {
                            event.stopPropagation();
                            if (!item.notificationId) return;

                            const nextResolved = event.target.checked;
                            if (nextResolved) {
                              setResolveConfirmNotificationId(
                                item.notificationId
                              );
                              return;
                            }

                            const id = item.notificationId;
                            setUpdatingStatusIds((prev) => [...prev, id]);

                            void (async () => {
                              try {
                                await mlInsightsHandler.updateMlInsightStatus(
                                  id,
                                  true
                                );
                                dispatch(
                                  showNotification({
                                    message: t('insight_marked_as_unresolved'),
                                    type: 'success',
                                  })
                                );
                              } catch (error) {
                                console.error(
                                  'Failed to update ML insight status:',
                                  error
                                );
                                dispatch(
                                  showNotification({
                                    message: t(
                                      'failed_to_update_insight_status_please_try_again'
                                    ),
                                    type: 'error',
                                  })
                                );
                              } finally {
                                setUpdatingStatusIds((prev) =>
                                  prev.filter((x) => x !== id)
                                );
                              }
                            })();
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderTop: `1px solid ${
              enableDarkTheme
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)'
            }`,
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: palette.textPrimary,
            }}
          >
            {`${page * rowsPerPage + 1} ${t('ag_grid_to')} ${Math.min(
              (page + 1) * rowsPerPage,
              tableData.length
            )} ${t('ag_grid_of')} ${tableData.length}`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => {
                setPage(0);
              }}
              disabled={page === 0}
              sx={{
                color: palette.textPrimary,
                '&:disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              ⟪
            </IconButton>
            <IconButton
              onClick={() => {
                setPage(page - 1);
              }}
              disabled={page === 0}
              sx={{
                color: palette.textPrimary,
                '&:disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              ‹
            </IconButton>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.textPrimary,
                mx: 1,
              }}
            >
              {`${t('ag_grid_page')} ${page + 1} ${t('ag_grid_of')} ${Math.ceil(
                tableData.length / rowsPerPage
              )}`}
            </Typography>
            <IconButton
              onClick={() => {
                setPage(page + 1);
              }}
              disabled={page >= Math.ceil(tableData.length / rowsPerPage) - 1}
              sx={{
                color: palette.textPrimary,
                '&:disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              ›
            </IconButton>
            <IconButton
              onClick={() => {
                setPage(Math.ceil(tableData.length / rowsPerPage) - 1);
              }}
              disabled={page >= Math.ceil(tableData.length / rowsPerPage) - 1}
              sx={{
                color: palette.textPrimary,
                '&:disabled': {
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.3)',
                },
              }}
            >
              ⟫
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={resolveConfirmNotificationId !== null}
        onClose={() => {
          setResolveConfirmNotificationId(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: palette.boxBackground,
            color: palette.textPrimary,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '16px' }}>
          {t('mark_as_resolved')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('do_you_want_to_mark_this_as_resolved')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setResolveConfirmNotificationId(null);
            }}
            sx={{
              color: palette.textPrimary,
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const id = resolveConfirmNotificationId;
              if (!id) return;
              setResolveConfirmNotificationId(null);
              setUpdatingStatusIds((prev) => [...prev, id]);

              void (async () => {
                try {
                  await mlInsightsHandler.updateMlInsightStatus(id, false);
                  dispatch(
                    showNotification({
                      message: t('insight_marked_as_resolved'),
                      type: 'success',
                    })
                  );
                } catch (error) {
                  console.error('Failed to update ML insight status:', error);
                  dispatch(
                    showNotification({
                      message: t(
                        'failed_to_update_insight_status_please_try_again'
                      ),
                      type: 'error',
                    })
                  );
                } finally {
                  setUpdatingStatusIds((prev) => prev.filter((x) => x !== id));
                }
              })();
            }}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
